import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import debouncer from '../utils/requestDebouncer';

/**
 * Hook para gestionar permisos y límites de uso del usuario
 * Sincronizado con el backend para validación de planes
 */

// Definición de características por plan (debe coincidir con el backend)
// 🎁 FREE TRIAL: Acceso limitado a funcionalidades Pro
const PLAN_FEATURES = {
    // Free Trial (7 días): Acceso limitado con IA pero sin masivo/contactos
    trial: [
        'basic_rules',          // 3 Reglas
        'single_bot',           // 1 Usuario
        'basic_analytics',      // Analytics básico
        'liveChat',             // ✅ Chat en vivo
        'media_library',        // ✅ Biblioteca de medios (50MB)
        'ai_semantic_search',   // ✅ Búsqueda semántica con IA
        'ai_intent',            // ✅ Intención de IA
        'gen_ai',               // ✅ IA Generativa
        'anti_ban',             // ✅ Sistema Anti-Ban
        // ❌ SIN contacts
        // ❌ SIN mass_messaging
        // ❌ SIN intelligent_flows
        // ❌ SIN ai_assistant
        // ❌ SIN flow_analytics
    ],
    // Basic: Bot clásico sin IA semántica ni analytics avanzados
    basic: [
        'basic_rules', 'single_bot', 'basic_analytics',
        'ai_semantic_search',   // ✅ Búsqueda semántica
        'ai_intent',            // ✅ Intención de IA
        'gen_ai',               // ✅ IA Generativa
        'anti_ban',             // ✅ Sistema Anti-Ban
        'media_library'         // ✅ Biblioteca de medios
        // ❌ SIN contacts
        // ❌ SIN mass_messaging
        // ❌ SIN liveChat
    ],
    // Pro: Bot inteligente con IA semántica + Analytics avanzados
    pro: [
        'basic_rules', 'advanced_rules', 'single_bot', 'contacts', 
        'advanced_analytics', 'mass_messaging', 'media_library', 
        'ai_assistant', 'intelligent_flows', 'scheduled_messages', 
        'contact_segments', 'liveChat', 
        'ai_analytics',       // ✅ Análisis de IA
        'flow_analytics',     // ✅ Analytics de Flujos
        'ai_semantic_search', // ✅ Búsqueda semántica con IA
        'ai_intent',          // ✅ Intención de IA
        'gen_ai',             // ✅ IA Generativa
        'anti_ban',           // ✅ Sistema Anti-Ban
        'persistent_session'  // ✅ Sesión Persistente
    ],
    business: [
        'basic_rules', 'advanced_rules', 'multi_bot', 'contacts', 
        'advanced_analytics', 'mass_messaging', 'media_library', 
        'ai_assistant', 'intelligent_flows', 'scheduled_messages', 
        'contact_segments', 'api_access', 'webhooks', 'custom_integrations', 
        'priority_support', 'team_management', 'liveChat', 
        'ai_analytics', 'flow_analytics', 'ai_semantic_search',
        'ai_intent', 'gen_ai', 'anti_ban', 'persistent_session',
        'taxiDispatch'          // 🚕 Módulo Taxi Dispatch
    ],
    enterprise: ['all']
};

// Definición de límites por plan
const PLAN_LIMITS = {
    // 🎁 Free Trial: Límites ajustados (1400 total)
    trial: {
        messagesPerMonth: 1400,    // 200/día * 7 días
        conversationsPerMonth: 1400,
        maxRules: 3,               // Limitado a 3 flujos
        maxContacts: 0,            // Sin contactos
        maxBots: 1,
        maxUsers: 1
    },
    basic: {
        messagesPerMonth: 14400,   // 480/día * 30 días
        conversationsPerMonth: 14400,
        maxRules: 10,
        maxContacts: 0,            // Sin contactos
        maxBots: 1,
        maxUsers: 1
    },
    pro: {
        messagesPerMonth: 72000,   // 2,400/día * 30 días
        conversationsPerMonth: 72000,
        maxRules: 30,
        maxContacts: 5000,
        maxBots: 1,
        maxUsers: 3
    },
    business: {
        messagesPerMonth: 144000,  // 4,800/día * 30 días
        conversationsPerMonth: 144000,
        maxRules: 100,
        maxContacts: 25000,
        maxBots: 3,
        maxUsers: 10
    },
    enterprise: {
        messagesPerMonth: -1,
        conversationsPerMonth: -1,
        maxRules: -1,
        maxContacts: -1,
        maxBots: -1,
        maxUsers: -1
    }
};

/**
 * Hook para gestionar permisos y límites del usuario
 */
export const usePermissions = () => {
    const { currentUser } = useAuth();
    const [subscription, setSubscription] = useState(null);
    const [usage, setUsage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [alerts, setAlerts] = useState([]);

    useEffect(() => {
        if (!currentUser) return;

        // Cargar resumen inicial
        loadUsageSummary();

        // ⚡ OPTIMIZACIÓN CRÍTICA: Aumentar de 30s a 5 minutos
        // Esto reduce ~90% de lecturas de Firebase por polling
        const intervalId = setInterval(() => {
            loadUsageSummary();
        }, 300000); // 5 minutos

        // Cleanup: detener polling cuando el componente se desmonte
        return () => clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);

    const loadUsageSummary = async () => {
        try {
            // ⚡ OPTIMIZADO: Usar debouncer para evitar requests duplicados
            const data = await debouncer.execute(
                `usage-summary-${currentUser.uid}`,
                async () => {
                    const token = await currentUser.getIdToken();
                    const response = await fetch(`${API_URL}/usage/summary`, {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });

                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}`);
                    }

                    return await response.json();
                },
                { useCache: true, cacheDuration: 5000 } // Cache de 5 segundos
            );

            // El endpoint devuelve directamente los datos, no en data.summary
            setSubscription(data);
            setUsage(data.usage);
            setAlerts(data.alerts || []);
        } catch (error) {
            console.error('Error cargando resumen de uso:', error);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Verificar si el usuario tiene acceso a una característica
     */
    const hasFeature = (featureName) => {
        if (!subscription) return false;
        const planId = (subscription.planId || 'trial').toLowerCase();
        const features = PLAN_FEATURES[planId] || [];
        return features.includes('all') || features.includes(featureName);
    };

    /**
     * Verificar si el usuario alcanzó un límite
     */
    const hasReachedLimit = (limitType) => {
        if (!subscription || !usage) return false;
        const planId = (subscription.planId || 'trial').toLowerCase();
        const limits = PLAN_LIMITS[planId];
        
        const limit = limits[limitType];
        if (limit === -1) return false; // Ilimitado

        let current = 0;
        switch(limitType) {
            case 'messagesPerMonth':
                current = usage.messages?.current || 0;
                break;
            case 'conversationsPerMonth':
                current = usage.conversations?.current || 0;
                break;
            case 'maxRules':
                current = usage.rules?.current || 0;
                break;
            case 'maxContacts':
                current = usage.contacts?.current || 0;
                break;
            default:
                return false;
        }

        return current >= limit;
    };

    /**
     * Obtener porcentaje de uso de un límite
     */
    const getUsagePercentage = (limitType) => {
        if (!subscription || !usage) return 0;
        const planId = (subscription.planId || 'trial').toLowerCase();
        const limits = PLAN_LIMITS[planId];
        
        const limit = limits[limitType];
        if (limit === -1) return 0; // Ilimitado

        let current = 0;
        switch(limitType) {
            case 'messagesPerMonth':
                current = usage.messages?.current || 0;
                break;
            case 'conversationsPerMonth':
                current = usage.conversations?.current || 0;
                break;
            case 'maxRules':
                current = usage.rules?.current || 0;
                break;
            case 'maxContacts':
                current = usage.contacts?.current || 0;
                break;
            default:
                return 0;
        }

        return Math.min(100, Math.round((current / limit) * 100));
    };

    /**
     * Obtener límite de un tipo
     */
    const getLimit = (limitType) => {
        if (!subscription) return 0;
        const planId = (subscription.planId || 'trial').toLowerCase();
        const limits = PLAN_LIMITS[planId];
        return limits[limitType] || 0;
    };

    /**
     * Obtener uso actual de un tipo
     */
    const getCurrentUsage = (limitType) => {
        if (!usage) return 0;
        
        switch(limitType) {
            case 'messagesPerMonth':
                return usage.messages?.current || 0;
            case 'conversationsPerMonth':
                return usage.conversations?.current || 0;
            case 'maxRules':
                return usage.rules?.current || 0;
            case 'maxContacts':
                return usage.contacts?.current || 0;
            default:
                return 0;
        }
    };

    /**
     * Verificar si está cerca de un límite (>= 80%)
     */
    const isNearLimit = (limitType) => {
        return getUsagePercentage(limitType) >= 80;
    };

    /**
     * Recargar datos de uso
     */
    const refresh = () => {
        loadUsageSummary();
    };

    return {
        // Estado
        subscription,
        usage,
        loading,
        alerts,
        
        // Funciones
        hasFeature,
        hasReachedLimit,
        getUsagePercentage,
        getLimit,
        getCurrentUsage,
        isNearLimit,
        refresh,
        
        // Datos útiles
        planId: subscription?.planId || 'trial',
        features: subscription?.features || [],
        limits: subscription?.limits || {}
    };
};

export default usePermissions;
